import type { User } from '@/types/User'

interface ApiResponse {
  status_code: number
  ok: boolean
}

export interface GenericResponse extends ApiResponse {
  result: any
}

export interface MeResponse extends ApiResponse {
  user: User
}

export interface VendorsResponse extends ApiResponse {
  vendors: any[]
}

export interface CreateVendor {
  name: string
  phone?: string
  email?: string
  point_of_contact?: string
  vendor_type: string
  physical_address?: string
  notes?: string
  active: boolean
}

export interface CreateVenue {
  name: string
  manager_name: string
  manager_email: string
  manager_phone: string
}

export class ApiClient {

  async getMe(): Promise<MeResponse> {
    const response = await fetch('/api/auth/me', {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })

    return {
      status_code: response.status,
      ok: response.ok,
      user: await response.json(),
    }
  }

  async getAllVenues(): Promise<GenericResponse> {
    const response = await fetch('/api/venues', {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })
    
    const result = await response.json()
    return {
      status_code: response.status,
      ok: response.ok,
      result: result.venues
    }
  }

  async getVenueById(venueId: string): Promise<GenericResponse | null> {
    const response = await fetch(`/api/venues/${venueId}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })
    
    if (!response.ok) {
      return null
    }
    const result = await response.json()
    return {
      status_code: response.status,
      ok: response.ok,
      result: result.venue,
    }
  }

  async getVenueManagers(venueId: string): Promise<GenericResponse | null> {
    const response = await fetch(`/api/venues/${venueId}/managers`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })

    if (!response.ok) {
      return {
        status_code: response.status,
        ok: response.ok,
        result: [],
      }
    }
    const result = await response.json()
    return {
      status_code: response.status,
      ok: response.ok,
      result: result.managers,
    }
  }

  async addManagerToVenue(venueId: string, email: string): Promise<ApiResponse> {
    const response = await fetch(`/api/venues/${venueId}/add_manager`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email }),
    })
    
    return {
      status_code: response.status,
      ok: response.ok
    }
  }

  createVenue(venueData: CreateVenue): Promise<ApiResponse> {
    return fetch('/api/venues', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(venueData),
    }).then((response) => {
      return {
        status_code: response.status,
        ok: response.ok,
      }
    })
  }

  async getVenueUsers(venueId: string): Promise<GenericResponse | null> {
    const response = await fetch(`/api/venues/${venueId}/users`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })
    
    if (!response.ok) {
      return {
        status_code: response.status,
        ok: response.ok,
        result: null,
      }
    }
    const result = await response.json()
    return {
      status_code: response.status,
      ok: response.ok,
      result: result.users,
    }
  }

  async addVenueUser(venueId: string, email: string): Promise<ApiResponse> {
    const response = await fetch(`/api/venues/${venueId}/users`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email }),
    })
    
    return {
      status_code: response.status,
      ok: response.ok
    }
  }

  async removeVenueUser(venueId: string, id: string): Promise<ApiResponse> {
    const response = await fetch(`/api/venues/${venueId}/users/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })

    return {
      status_code: response.status,
      ok: response.ok,
    }
  }

  async getAllVendors(venueId: string, active_only: boolean = false): Promise<VendorsResponse> {
    const response = await fetch(`/api/vendors?venue_id=${venueId}&active_only=${active_only}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })

    return {
      status_code: response.status,
      ok: response.ok,
      vendors: (await response.json()).vendors,
    }
  }

  async searchVendors(search: string): Promise<GenericResponse> {
    const params = new URLSearchParams({ search })
    const response = await fetch(`/api/vendors/search?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })
    
    return {
      status_code: response.status,
      ok: response.ok,
      result: (await response.json()).vendors,
    }
  }

  async getVendorById(vendorId: string): Promise<GenericResponse | null> {
    const response = await fetch(`/api/vendors/${vendorId}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })

    if (!response.ok) {
      return {
        status_code: response.status,
        ok: response.ok,
        result: null,
      }
    }
    
    const result = await response.json()
    return {
      status_code: response.status,
      ok: response.ok,
      result: result.vendor,
    }
  }

  async createVendor(vendorData: CreateVendor): Promise<GenericResponse> {
    const response = await fetch('/api/vendors', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(vendorData),
    })

    const json = await response.json()

    return {
      status_code: response.status,
      ok: response.ok,
      result: json.vendor,
    }
  }

  async updateVendor(vendorId: string, vendorData: Partial<CreateVendor>): Promise<GenericResponse> {
    const response = await fetch(`/api/vendors/${vendorId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(vendorData),
    })
    
    const json = await response.json()
    return {
      status_code: response.status,
      ok: response.ok,
      result: json.vendor,
    }
  }

  async deleteVendor(vendorId: string): Promise<ApiResponse> {
    const response = await fetch(`/api/vendors/${vendorId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })
    
    return {
      status_code: response.status,
      ok: response.ok
    }
  }

  async addVendorToVenue(venueId: string, vendorId: string): Promise<ApiResponse> {
    const response = await fetch(`/api/vendors/${vendorId}/add-to-venue/${venueId}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ vendor_id: vendorId }),
    })
    
    return {
      status_code: response.status,
      ok: response.ok
    }
  }

  async removeVendorFromVenue(vendorId: string, venueId: string): Promise<ApiResponse> {
    const response = await fetch(`/api/vendors/${vendorId}/remove-from-venue/${venueId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })

    return {
      status_code: response.status,
      ok: response.ok
    }
  }

  async vendorTypeAutocomplete(search: string): Promise<GenericResponse> {
    const params = new URLSearchParams({ search })
    const response = await fetch(`/api/vendors/type?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })

    if (!response.ok) {
      return {
        status_code: response.status,
        ok: response.ok,
        result: [],
      }
    }

    let result = await response.json()

    let strings = []
    for (const item of result.vendor_types) {
      strings.push(item)
    }

    return {
      status_code: response.status,
      ok: response.ok,
      result: strings,
    }
  }
}
